import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "@store/index";
import { trpc } from "@utils/api";

export interface ReportDefinition {
  id: string;
  handle: string;
  name: string;
  description: string;
  created_at: string;
  version: number;
}

export interface ReportDefinitionFull extends ReportDefinition {
  template_yaml: string;
  global_arguments: Record<string, unknown> | null;
  status: string;
  updated_at: string;
}

export interface ReportsState {
  definitions: {
    isLoading: boolean;
    data: ReportDefinition[];
  };
  currentDefinition: {
    isLoading: boolean;
    data: ReportDefinitionFull | null;
    error: string | null;
  };
  selectedDefinition: ReportDefinition | null;
  editor: {
    yamlContent: string;
    previewHtml: string | null;
    error: string | null;
    isSaving: boolean;
    isValidating: boolean;
    isGenerating: boolean;
  };
}

const initialState: ReportsState = {
  definitions: {
    isLoading: false,
    data: [],
  },
  currentDefinition: {
    isLoading: false,
    data: null,
    error: null,
  },
  selectedDefinition: null,
  editor: {
    yamlContent: "",
    previewHtml: null,
    error: null,
    isSaving: false,
    isValidating: false,
    isGenerating: false,
  },
};

// Async Thunks
export const getDefinitions = createAsyncThunk(
  "reports/getDefinitions",
  async () => {
    const result = await trpc.reports.listDefinitions.query({
      limit: 50,
      page: 1,
    });
    return (result.items ?? []) as ReportDefinition[];
  },
);

export const getDefinition = createAsyncThunk(
  "reports/getDefinition",
  async (params: { definitionId: string }) => {
    const result = await trpc.reports.getDefinition.query({
      definitionId: params.definitionId,
    });
    if (!result.success || !result.data) {
      throw new Error(
        result.errors?.[0]?.message ?? "Failed to fetch definition",
      );
    }
    return result.data as ReportDefinitionFull;
  },
);

export const validateTemplate = createAsyncThunk<
  { valid: boolean; errors?: string[] },
  void,
  { state: RootState }
>("reports/validateTemplate", async (_, { getState }) => {
  const state = getState();
  const yamlContent = state.reports.editor.yamlContent;

  if (!yamlContent.trim()) {
    return { valid: false, errors: ["Please provide YAML content"] };
  }

  const result = await trpc.reports.validateTemplate.mutate({
    yaml: yamlContent,
  });
  return {
    valid: result.valid,
    errors: result.errors ?? undefined,
  };
});

export const defineReport = createAsyncThunk<
  { success: boolean; definitionId?: string; errors?: string[] },
  void,
  { state: RootState }
>("reports/defineReport", async (_, { getState }) => {
  const state = getState();
  const yamlContent = state.reports.editor.yamlContent;

  if (!yamlContent.trim()) {
    return { success: false, errors: ["Please provide YAML content"] };
  }

  const result = await trpc.reports.defineReport.mutate({ yaml: yamlContent });
  return {
    success: result.success,
    definitionId: result.definitionId,
    errors: result.errors,
  };
});

export const generateReport = createAsyncThunk<
  { success: boolean; html?: string; errors?: string[] },
  { templateHandle: string; arguments?: Record<string, unknown> }
>("reports/generateReport", async ({ templateHandle, arguments: args }) => {
  const result = await trpc.reports.generateReport.mutate({
    templateHandle,
    arguments: args || {},
    format: "static",
  });
  return {
    success: result.success,
    html: result.html,
    errors: result.errors,
  };
});

// Slice
export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setYamlContent(state, action: PayloadAction<string>) {
      state.editor.yamlContent = action.payload;
    },
    setSelectedDefinition(
      state,
      action: PayloadAction<ReportDefinition | null>,
    ) {
      state.selectedDefinition = action.payload;
    },
    setPreviewHtml(state, action: PayloadAction<string | null>) {
      state.editor.previewHtml = action.payload;
    },
    clearEditor(state) {
      state.editor = {
        yamlContent: "",
        previewHtml: null,
        error: null,
        isSaving: false,
        isValidating: false,
        isGenerating: false,
      };
      state.selectedDefinition = null;
    },
    clearError(state) {
      state.editor.error = null;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.editor.error = action.payload;
    },
    clearCurrentDefinition(state) {
      state.currentDefinition = {
        isLoading: false,
        data: null,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    // getDefinitions
    builder
      .addCase(getDefinitions.pending, (state) => {
        state.definitions.isLoading = true;
      })
      .addCase(getDefinitions.fulfilled, (state, action) => {
        state.definitions.isLoading = false;
        state.definitions.data = action.payload;
      })
      .addCase(getDefinitions.rejected, (state) => {
        state.definitions.isLoading = false;
      });

    // validateTemplate
    builder
      .addCase(validateTemplate.pending, (state) => {
        state.editor.isValidating = true;
        state.editor.error = null;
      })
      .addCase(validateTemplate.fulfilled, (state, action) => {
        state.editor.isValidating = false;
        if (!action.payload.valid) {
          state.editor.error =
            action.payload.errors?.join(", ") ?? "Invalid template";
        } else {
          state.editor.error = null;
        }
      })
      .addCase(validateTemplate.rejected, (state, action) => {
        state.editor.isValidating = false;
        state.editor.error = action.error.message ?? "Validation failed";
      });

    // defineReport
    builder
      .addCase(defineReport.pending, (state) => {
        state.editor.isSaving = true;
        state.editor.error = null;
      })
      .addCase(defineReport.fulfilled, (state, action) => {
        state.editor.isSaving = false;
        if (!action.payload.success) {
          state.editor.error =
            action.payload.errors?.join(", ") ?? "Failed to save template";
        } else {
          state.editor.yamlContent = "";
          state.editor.error = null;
        }
      })
      .addCase(defineReport.rejected, (state, action) => {
        state.editor.isSaving = false;
        state.editor.error = action.error.message ?? "Failed to save template";
      });

    // generateReport
    builder
      .addCase(generateReport.pending, (state) => {
        state.editor.isGenerating = true;
        state.editor.error = null;
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.editor.isGenerating = false;
        if (action.payload.success && action.payload.html) {
          state.editor.previewHtml = action.payload.html;
        } else {
          state.editor.error =
            action.payload.errors?.join(", ") ?? "Failed to generate report";
        }
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.editor.isGenerating = false;
        state.editor.error =
          action.error.message ?? "Failed to generate report";
      });

    // getDefinition
    builder
      .addCase(getDefinition.pending, (state) => {
        state.currentDefinition.isLoading = true;
        state.currentDefinition.error = null;
      })
      .addCase(getDefinition.fulfilled, (state, action) => {
        state.currentDefinition.isLoading = false;
        state.currentDefinition.data = action.payload;
        state.currentDefinition.error = null;
      })
      .addCase(getDefinition.rejected, (state, action) => {
        state.currentDefinition.isLoading = false;
        state.currentDefinition.error =
          action.error.message ?? "Failed to fetch definition";
      });
  },
});

// Selectors
export const selectDefinitions = (state: RootState) =>
  state.reports.definitions;
export const selectCurrentDefinition = (state: RootState) =>
  state.reports.currentDefinition;
export const selectSelectedDefinition = (state: RootState) =>
  state.reports.selectedDefinition;
export const selectEditor = (state: RootState) => state.reports.editor;
export const selectIsLoading = (state: RootState) =>
  state.reports.definitions.isLoading;

// Action creators
export const {
  setYamlContent,
  setSelectedDefinition,
  setPreviewHtml,
  clearEditor,
  clearError,
  setError,
  clearCurrentDefinition,
} = reportsSlice.actions;

export default reportsSlice.reducer;
